import {
	useEffect,
	useId,
	useRef,
	useState,
	type ComponentProps,
	type CSSProperties,
} from 'react'

import { ErrorList } from '#app/components/form/error-list.tsx'
import { type ListOfErrors } from '#app/components/form/types.ts'
import { Label } from '#app/components/ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import { cn } from '#app/utils/misc.tsx'

type Option = {
	value: string
	label: string
}

type SelectFieldSelectProps = ComponentProps<'select'> & {
	onValueChange?: (value: string) => void
}

interface Props {
	labelProps: ComponentProps<'label'>
	selectProps: SelectFieldSelectProps
	options: ReadonlyArray<Option>
	descriptionProps?: ComponentProps<'p'>
	errors?: ListOfErrors
	placeholder?: string
	className?: string
}

const hiddenSelectStyle: CSSProperties = {
	display: 'none',
}

function toSingleValue(
	value:
		| ComponentProps<'select'>['value']
		| ComponentProps<'select'>['defaultValue'],
): string | undefined {
	if (typeof value === 'string') return value
	if (typeof value === 'number') return String(value)
	if (Array.isArray(value)) return value[0]
	return undefined
}

function setNativeSelectValue(element: HTMLSelectElement, nextValue: string) {
	const descriptor = Object.getOwnPropertyDescriptor(
		element.constructor.prototype,
		'value',
	)
	if (descriptor?.set) {
		descriptor.set.call(element, nextValue)
	} else {
		element.value = nextValue
	}
}

function dispatchInputEvents(element: HTMLSelectElement) {
	element.dispatchEvent(new Event('input', { bubbles: true }))
	element.dispatchEvent(new Event('change', { bubbles: true }))
}

export function SelectField({
	labelProps,
	selectProps,
	options,
	descriptionProps,
	errors,
	placeholder = 'Select an option',
	className,
}: Props) {
	const fallbackId = useId()
	const {
		className: selectClassName,
		id: providedId,
		defaultValue,
		value,
		multiple,
		onValueChange,
		...restSelectProps
	} = selectProps
	const id = providedId ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	// All hooks must be called before any conditional returns
	const selectRef = useRef<HTMLSelectElement>(null)
	const initialValue = toSingleValue(value ?? defaultValue) ?? ''
	const [selectValue, setSelectValue] = useState(initialValue)

	const isControlled = value !== undefined

	useEffect(() => {
		if (multiple) return
		if (isControlled) {
			const next = toSingleValue(value) ?? ''
			setSelectValue(next)
		}
	}, [isControlled, multiple, value])

	useEffect(() => {
		if (multiple || isControlled) return
		const next = toSingleValue(defaultValue) ?? ''
		setSelectValue(next)
	}, [defaultValue, isControlled, multiple])

	useEffect(() => {
		if (multiple) return
		const element = selectRef.current
		if (!element) return
		setNativeSelectValue(element, selectValue)
	}, [multiple, selectValue])

	if (multiple) {
		const resolvedValue =
			value ?? (defaultValue !== undefined ? defaultValue : [])

		return (
			<div className={cn('space-y-1.5', className)}>
				<Label htmlFor={id} {...labelProps} />
				<select
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					multiple
					className={cn(
						'border-input bg-background ring-offset-background focus-visible:ring-ring flex h-8 w-full rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
						selectClassName,
					)}
					value={resolvedValue as ComponentProps<'select'>['value']}
					{...restSelectProps}
				>
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
				{descriptionProps ? (
					<p
						{...descriptionProps}
						className={cn(
							'text-muted-foreground text-xs',
							descriptionProps.className,
						)}
					/>
				) : null}
				<div className="min-h-[16px] pt-1 pl-4">
					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
				</div>
			</div>
		)
	}

	const {
		children: labelChildren,
		htmlFor: _ignoredHtmlFor,
		id: providedLabelId,
		...restLabelProps
	} = labelProps
	const labelId = providedLabelId ?? `${id}-label`
	const triggerId = `${id}-trigger`

	const EMPTY_VALUE = '__select-empty__'

	const applyValue = (rawValue: string, dispatchEvents: boolean) => {
		const nextValue = rawValue === EMPTY_VALUE ? '' : rawValue
		if (isControlled) {
			const controlledValue = toSingleValue(value)
			if (
				controlledValue !== undefined &&
				controlledValue !== '' &&
				nextValue === '' &&
				controlledValue !== nextValue
			) {
				return
			}
		}
		const element = selectRef.current
		if (nextValue === selectValue || (element && element.value === nextValue)) {
			return
		}
		setSelectValue(nextValue)
		if (!element) return
		setNativeSelectValue(element, nextValue)
		if (dispatchEvents) {
			dispatchInputEvents(element)
		}
	}

	const handleValueChange = (rawValue: string) => {
		applyValue(rawValue, true)
		const nextValue = rawValue === EMPTY_VALUE ? '' : rawValue
		if (typeof onValueChange === 'function') {
			onValueChange(nextValue)
		}
	}

	const disabled = restSelectProps.disabled ?? false
	const required = restSelectProps.required ?? false
	const { style: nativeStyle, ...restNativeProps } = restSelectProps
	const nativeSelectId = `${id}-native`

	return (
		<div className={cn('space-y-1.5', className)}>
			<Label id={labelId} htmlFor={triggerId} {...restLabelProps}>
				{labelChildren}
			</Label>
			<div className="relative">
				<Select
					value={selectValue === '' ? EMPTY_VALUE : selectValue}
					onValueChange={handleValueChange}
					disabled={disabled}
				>
					<SelectTrigger
						id={triggerId}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						aria-labelledby={labelId}
						aria-required={required ? true : undefined}
						className={cn(selectClassName)}
						hasError={Boolean(errorId)}
					>
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent className="max-h-[15rem] overflow-y-auto">
						<SelectItem value={EMPTY_VALUE} disabled className="hidden">
							{placeholder}
						</SelectItem>
						{options.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<select
					ref={selectRef}
					id={nativeSelectId}
					value={selectValue}
					onChange={(event) => {
						const raw = event.target.value
						applyValue(raw, false)
						const nextValue = raw === '' ? '' : raw
						if (typeof onValueChange === 'function') {
							onValueChange(nextValue)
						}
					}}
					tabIndex={-1}
					aria-hidden="true"
					style={{
						...hiddenSelectStyle,
						...nativeStyle,
					}}
					{...restNativeProps}
				>
					<option value="" disabled hidden>
						{placeholder}
					</option>
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</div>
			{descriptionProps ? (
				<p
					{...descriptionProps}
					className={cn(
						'text-muted-foreground text-xs',
						descriptionProps.className,
					)}
				/>
			) : null}
			<div className="min-h-[16px]">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
